import { useState } from "react";
import { ChevronRight, Pencil, Save, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import { putOverride } from "../lib/overlay";
import { getTags, nextExperienceWrap } from "../lib/queries";
import { TagEditor } from "./TagEditor";
import type { Experience } from "../types";

const eqTags = (a: string[], b: string[]) => JSON.stringify(a) === JSON.stringify(b);

/** Admin-only affordance on an experience page: an Edit button that expands the inline editor. */
export function AdminEditExperience({ experience }: { experience: Experience }) {
  const { isAdmin } = useAuth();
  const [editing, setEditing] = useState(false);
  if (!isAdmin) return null;
  return editing ? (
    <ExperienceEditor experience={experience} onClose={() => setEditing(false)} />
  ) : (
    <div className="eng-edit-bar">
      <button type="button" className="btn btn-sm" onClick={() => setEditing(true)}>
        <Pencil size={14} /> Edit experience
      </button>
    </div>
  );
}

function ExperienceEditor({ experience, onClose }: { experience: Experience; onClose: () => void }) {
  const { getApiToken } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState(experience.company);
  const [role, setRole] = useState(experience.role);
  const [summary, setSummary] = useState(experience.summary);
  const [narrative, setNarrative] = useState(experience.narrative ?? "");
  const [industries, setIndustries] = useState<string[]>(experience.industries);
  const [technologies, setTechnologies] = useState<string[]>(experience.technologies);
  const [skills, setSkills] = useState<string[]>(experience.skills);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const doSave = async (): Promise<boolean> => {
    setSaving(true);
    setErr(null);
    const token = await getApiToken();
    if (!token) {
      setErr("Your sign-in session expired — please sign in again.");
      setSaving(false);
      return false;
    }
    const fields: Record<string, string> = {};
    if (company !== experience.company) fields.company = company;
    if (role !== experience.role) fields.role = role;
    if (summary !== experience.summary) fields.summary = summary;
    if (narrative !== (experience.narrative ?? "")) fields.narrative = narrative;
    if (!eqTags(industries, experience.industries)) fields.industries = JSON.stringify(industries);
    if (!eqTags(technologies, experience.technologies)) fields.technologies = JSON.stringify(technologies);
    if (!eqTags(skills, experience.skills)) fields.skills = JSON.stringify(skills);
    // Experiences have no draft lifecycle — publish edits so visitors see them.
    const ok = await putOverride(experience.id, { stage: "published", fields }, token);
    setSaving(false);
    if (!ok) setErr("Save failed — your session may have expired. Sign out, sign back in, and retry.");
    return ok;
  };
  const save = async () => {
    if (await doSave()) onClose();
  };
  const saveAndNext = async () => {
    if (!(await doSave())) return;
    const nx = nextExperienceWrap(experience.id);
    if (nx && nx.id !== experience.id) navigate(`/experience/${nx.id}`);
    else onClose();
  };

  return (
    <div className="eng-editor card card-pad">
      <div className="eng-editor-head">
        <strong>Edit experience</strong>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Close" style={{ marginLeft: "auto" }}>
          <X size={16} />
        </button>
      </div>
      <label className="eng-field">
        <span>Company</span>
        <input value={company} onChange={(e) => setCompany(e.target.value)} />
      </label>
      <label className="eng-field">
        <span>Role</span>
        <input value={role} onChange={(e) => setRole(e.target.value)} />
      </label>
      <label className="eng-field">
        <span>Summary</span>
        <textarea rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} />
      </label>
      <label className="eng-field">
        <span>Narrative</span>
        <textarea rows={6} value={narrative} onChange={(e) => setNarrative(e.target.value)} />
      </label>
      <div className="eng-tags">
        <TagEditor label="Industries" singular="industry" value={industries} options={getTags("industry")} onChange={setIndustries} />
        <TagEditor label="Technologies" singular="technology" value={technologies} options={getTags("technology")} onChange={setTechnologies} />
        <TagEditor label="Skills" singular="skill" value={skills} options={getTags("skill")} onChange={setSkills} />
      </div>
      {err && <p className="eng-err">{err}</p>}
      <div className="eng-actions">
        <button type="button" className="btn btn-sm" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn btn-sm" onClick={saveAndNext} disabled={saving}>
          <ChevronRight size={14} /> Save &amp; Next
        </button>
        <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={saving}>
          <Save size={14} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
